import { CONTRACT_TYPE_LABELS, CONTRACT_TYPES } from "@etudescarre/constants";
import {
  Button,
  Checkbox,
  Drawer,
  Select,
  type SelectOption,
} from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { fetchAllCompanies, fetchAllYards } from "./components";

const CONTRACT_TYPE_OPTIONS: SelectOption[] = CONTRACT_TYPES.map((value) => ({
  label: CONTRACT_TYPE_LABELS[value],
  value,
}));

type Contract = {
  id: number;
  yardId: number;
  customerId: number | null;
  performerId: number;
  groupId: number | null;
  type: "amo" | "ct" | "sps" | "moe" | "travaux_n_1" | "travaux_n_2";
  shortName: string | null;
  isActive: boolean;
  isLead: boolean;
  delayForNoticeDays: number | null;
};

type ContractFormData = Omit<Contract, "id">;

export type ContractFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contract?: Contract;
  onSuccess?: () => void;
};

export function ContractForm({
  open,
  onOpenChange,
  contract,
  onSuccess,
}: ContractFormProps) {
  const [yardOptions, setYardOptions] = useState<SelectOption[]>([]);
  const [companyOptions, setCompanyOptions] = useState<SelectOption[]>([]);
  const [groupOptions, setGroupOptions] = useState<SelectOption[]>([]);
  const [isLoadingYards, setIsLoadingYards] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  const isEdit = !!contract;

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<ContractFormData>({
    defaultValues: {
      yardId: contract?.yardId ?? 0,
      performerId: contract?.performerId ?? 0,
      customerId: contract?.customerId ?? null,
      groupId: contract?.groupId ?? null,
      type: contract?.type ?? "amo",
      shortName: contract?.shortName ?? null,
      isActive: contract?.isActive ?? true,
      isLead: contract?.isLead ?? false,
      delayForNoticeDays: contract?.delayForNoticeDays ?? null,
    },
  });

  const yardId = watch("yardId");
  const performerId = watch("performerId");
  const customerId = watch("customerId");
  const groupId = watch("groupId");
  const contractType = watch("type");
  const isActive = watch("isActive");
  const isLead = watch("isLead");

  const fetchYards = useCallback(async () => {
    setIsLoadingYards(true);
    const { yards } = await fetchAllYards();
    if (yards) {
      setYardOptions(
        yards.map((y) => ({ label: y.name, value: String(y.id) })),
      );
    }
    setIsLoadingYards(false);
  }, []);

  const fetchCompanies = useCallback(async () => {
    setIsLoadingCompanies(true);
    const { companies } = await fetchAllCompanies();
    if (isNotNil(companies)) {
      setCompanyOptions(
        companies.map((c) => ({
          label: c.name,
          value: String(c.id),
        })),
      );
    }
    setIsLoadingCompanies(false);
  }, []);

  const fetchGroups = useCallback(async (selectedYardId: number) => {
    if (!selectedYardId) {
      setGroupOptions([]);
      return;
    }
    // Groups are fetched from contracts - use the yard filter to find groups
    // Since there's no dedicated endpoint, we'll list all contracts for this yard
    // and extract unique groups
    const { data } = await apiClient.GET("/api/admin/contracts", {
      params: { query: { yardId: selectedYardId } },
    });
    if (data) {
      const unique = new Map<number, string>();
      for (const c of data.data.contracts) {
        if (c.group) {
          unique.set(c.group.id, c.group.name);
        }
      }
      setGroupOptions([
        { label: "Aucun groupe", value: "0" },
        ...[...unique.entries()].map(([id, name]) => ({
          label: name,
          value: String(id),
        })),
      ]);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchYards();
      fetchCompanies();
      reset({
        yardId: contract?.yardId ?? 0,
        performerId: contract?.performerId ?? 0,
        customerId: contract?.customerId ?? null,
        groupId: contract?.groupId ?? null,
        type: contract?.type ?? "amo",
        shortName: contract?.shortName ?? null,
        isActive: contract?.isActive ?? true,
        isLead: contract?.isLead ?? false,
        delayForNoticeDays: contract?.delayForNoticeDays ?? null,
      });
    }
  }, [open, contract, reset, fetchYards, fetchCompanies]);

  useEffect(() => {
    if (yardId && yardId > 0) {
      fetchGroups(yardId);
    } else {
      setGroupOptions([]);
    }
  }, [yardId, fetchGroups]);

  const onSubmit = async (data: ContractFormData) => {
    try {
      if (isEdit) {
        const { error } = await apiClient.PATCH("/api/admin/contracts/{id}", {
          params: { path: { id: contract.id } },
          body: {
            yardId: data.yardId,
            performerId: data.performerId,
            customerId: data.customerId ?? null,
            groupId: data.groupId ?? null,
            type: data.type,
            shortName: data.shortName?.trim() || null,
            isActive: data.isActive,
            isLead: data.isLead,
            delayForNoticeDays: data.delayForNoticeDays ?? null,
          },
        });

        if (error) {
          toaster.error({
            title: "Erreur",
            description: error.error || "Échec de la modification du contrat",
          });
          return;
        }

        toaster.success({
          title: "Succès",
          description: "Contrat modifié avec succès",
        });
      } else {
        const { error } = await apiClient.POST("/api/admin/contracts", {
          body: {
            yardId: data.yardId,
            performerId: data.performerId,
            customerId: data.customerId ?? null,
            groupId: data.groupId ?? null,
            type: data.type,
            shortName: data.shortName?.trim() || null,
            isActive: data.isActive,
            isLead: data.isLead,
            delayForNoticeDays: data.delayForNoticeDays ?? null,
          },
        });

        if (error) {
          toaster.error({
            title: "Erreur",
            description: error.error || "Échec de la création du contrat",
          });
          return;
        }

        toaster.success({
          title: "Succès",
          description: "Contrat créé avec succès",
        });
      }

      onSuccess?.();
      onOpenChange(false);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    }
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <div className="flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800">
              <div>
                <Drawer.Title>
                  {isEdit ? "Modifier le contrat" : "Créer un contrat"}
                </Drawer.Title>
                <Drawer.Description>
                  {isEdit
                    ? "Modifiez les informations du contrat"
                    : "Créez un nouveau contrat"}
                </Drawer.Description>
              </div>
              <Drawer.CloseTrigger />
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
                {/* Yard */}
                <div>
                  <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Chantier <span className="text-red-500">*</span>
                  </span>
                  <Select
                    options={yardOptions}
                    value={yardId ? [String(yardId)] : []}
                    onValueChange={(details) => {
                      const val = details.value[0];
                      setValue("yardId", val ? Number(val) : 0);
                      setValue("groupId", null);
                    }}
                    placeholder="Sélectionner un chantier"
                    disabled={isLoadingYards}
                    required
                  />
                </div>

                {/* Type */}
                <div>
                  <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Type <span className="text-red-500">*</span>
                  </span>
                  <Select
                    options={CONTRACT_TYPE_OPTIONS}
                    value={[contractType]}
                    onValueChange={(details) => {
                      const val = details.value[0] as ContractFormData["type"];
                      if (val) setValue("type", val);
                    }}
                    placeholder="Sélectionner un type"
                    // Chosen at creation only: the API refuses a change.
                    disabled={isEdit}
                  />
                </div>

                {/* Performer (required) */}
                <div>
                  <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Entreprise intervenante{" "}
                    <span className="text-red-500">*</span>
                  </span>
                  <Select
                    options={companyOptions}
                    value={performerId ? [String(performerId)] : []}
                    onValueChange={(details) => {
                      const val = details.value[0];
                      setValue("performerId", val ? Number(val) : 0);
                    }}
                    placeholder="Sélectionner une entreprise"
                    disabled={isLoadingCompanies}
                    required
                  />
                </div>

                {/* Customer (optional) */}
                <div>
                  <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Maître d'ouvrage (optionnel)
                  </span>
                  <Select
                    options={[
                      { label: "Aucun", value: "0" },
                      ...companyOptions,
                    ]}
                    value={customerId ? [String(customerId)] : ["0"]}
                    onValueChange={(details) => {
                      const val = details.value[0];
                      setValue(
                        "customerId",
                        val && val !== "0" ? Number(val) : null,
                      );
                    }}
                    placeholder="Sélectionner un maître d'ouvrage"
                    disabled={isLoadingCompanies}
                  />
                </div>

                {/* Group (optional) */}
                <div>
                  <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Groupe (optionnel)
                  </span>
                  <Select
                    options={groupOptions}
                    value={groupId ? [String(groupId)] : ["0"]}
                    onValueChange={(details) => {
                      const val = details.value[0];
                      setValue(
                        "groupId",
                        val && val !== "0" ? Number(val) : null,
                      );
                    }}
                    placeholder={
                      yardId
                        ? "Sélectionner un groupe"
                        : "Sélectionnez d'abord un chantier"
                    }
                    disabled={!yardId}
                  />
                </div>

                {/* Code */}
                <div>
                  <label
                    htmlFor="shortName"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Code contrat
                  </label>
                  <input
                    id="shortName"
                    type="text"
                    {...register("shortName")}
                    maxLength={50}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Ex: C001"
                  />
                </div>

                {/* Delay for notice */}
                <div>
                  <label
                    htmlFor="delayForNoticeDays"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Délai de notification (jours)
                  </label>
                  <input
                    id="delayForNoticeDays"
                    type="number"
                    min={0}
                    {...register("delayForNoticeDays", {
                      setValueAs: (v) => (v === "" ? null : Number(v)),
                    })}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Ex: 14"
                  />
                </div>

                {/* isActive */}
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={isActive}
                    onCheckedChange={(details) =>
                      setValue("isActive", details.checked === true)
                    }
                  />
                  <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Contrat actif
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={isLead}
                    onCheckedChange={(details) =>
                      setValue("isLead", details.checked === true)
                    }
                  />
                  <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Mandataire (boss)
                  </span>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex shrink-0 justify-end gap-3 border-t border-zinc-200 p-6 dark:border-zinc-800">
                <Button
                  type="button"
                  outline
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "En cours..." : isEdit ? "Modifier" : "Créer"}
                </Button>
              </div>
            </form>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
